import { PhysicalClientSearch, PhysicalClientStatusFilter } from "@/lib/api";
import { isValidCpf, normalizeCpf } from "@/lib/validators/cpf";
import { physicalClientsQueryKey } from "./component/physicalClientQueryKeys";

/**
 * Busca da lista de clientes PF. Um único campo de texto: se, sem a máscara, tiver um CPF válido, a busca é exata por
 * CPF; senão, por nome, e-mail ou código ERP. O CPF vai só no body do POST: nunca na URL nem na query key do
 * TanStack Query (que fica em memória e aparece nas devtools).
 */

export type SearchMode = "cpf" | "query" | "none";

export interface SearchFilters {
  status: PhysicalClientStatusFilter;
  customerGroupId?: number;
}

export const resolveSearchMode = (text: string): SearchMode => {
  const trimmed = text.trim();
  if (!trimmed) return "none";
  return isValidCpf(trimmed) ? "cpf" : "query";
};

/**
 * Corpo de POST /physical-clients/search. A busca por CPF ignora o filtro de situação (é a procura de um cadastro
 * específico, que pode estar inativo ou bloqueado).
 */
export const buildSearchBody = (
  text: string,
  filters: SearchFilters,
  page: number,
  pageSize: number,
): PhysicalClientSearch => {
  const mode = resolveSearchMode(text);
  const body: PhysicalClientSearch = {
    status: mode === "cpf" ? "All" : filters.status,
    page,
    pageSize,
  };
  if (filters.customerGroupId) body.customerGroupId = filters.customerGroupId;
  if (mode === "cpf") body.cpf = normalizeCpf(text);
  if (mode === "query") body.query = text.trim();
  return body;
};

/**
 * Chave da query da busca. No modo CPF, usa só um número que muda a cada CPF buscado (cpfSearchId), nunca o CPF:
 * a chave fica no cache em memória e pode ser inspecionada.
 */
export const buildSearchQueryKey = (
  text: string,
  filters: SearchFilters,
  page: number,
  pageSize: number,
  cpfSearchId: number,
) => {
  const mode = resolveSearchMode(text);
  const term = mode === "cpf" ? { cpfSearchId } : { query: text.trim() };
  return [...physicalClientsQueryKey, "search", { mode, ...term, ...filters, page, pageSize }] as const;
};
